import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicNavbar, PublicFooter } from "@/components/layout/PublicNav";
import { ArrowRight, Sparkles, ScrollText, Users, CalendarClock, BookOpen, BrainCircuit } from "lucide-react";

const KEYWORDS = {
  "ai-teaching-software": { h1: "AI Teaching Software for the modern online tutor", sub: "Everything from class scheduling to AI quizzes, lesson plans, paper checking and a public profile that brings students from Google." },
  "online-tuition-software": { h1: "The online tuition software online tutors actually love.", sub: "Run your entire tuition business with AI: classes, students, fees, recordings & reports." },
  "software-for-tutors": { h1: "Software for tutors built by people who teach.", sub: "Save 5+ hours a week with AI-generated quizzes, lessons and engagement reports." },
  "lms-for-online-teachers": { h1: "An LMS for online teachers — without the bloat.", sub: "Courses, lessons, quizzes, attendance and recordings in one premium console." },
  "ai-lms-for-teachers": { h1: "An AI LMS for teachers that does the heavy lifting.", sub: "Generate quizzes, lesson plans and homework with a click. Track engagement automatically." },
  "online-class-management-software": { h1: "Online class management software for tutors and coaching centres.", sub: "Schedule recurring classes, send auto-reminders, mark attendance, manage fees." },
  "teaching-dashboard-for-tutors": { h1: "A teaching dashboard for tutors — fast, calm, productive.", sub: "Beautiful overview cards, today's schedule, AI credits and quick actions in one screen." },
  "ai-teacher-assistant": { h1: "Your AI teacher assistant, on duty 24/7.", sub: "Engagement analysis, post-class reports, homework drafts and instant lesson revisions." },
  "virtual-classroom-software": { h1: "Virtual classroom software with AI built-in.", sub: "Built-in TS Platform live classes or your favourite meeting link — plus chat, whiteboard, attendance & AI panel." },
};

export default function LandingKeyword() {
  const { keyword } = useParams();
  const meta = KEYWORDS[keyword] || { h1: "AI-powered teaching platform", sub: "TeachingSoftware.ai helps online tutors run their entire tuition business with AI." };

  useEffect(() => {
    document.title = `${meta.h1} | TeachingSoftware.ai`;
  }, [meta.h1]);

  return (
    <div>
      <PublicNavbar />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-4">/ {keyword}</div>
        <h1 className="font-display font-bold text-4xl lg:text-6xl tracking-tighter max-w-4xl">{meta.h1}</h1>
        <p className="mt-6 text-lg text-neutral-700 max-w-2xl">{meta.sub}</p>
        <div className="mt-8 flex gap-3">
          <Link to="/signup" className="bg-[#FFD600] text-[#0A0C10] font-bold px-6 py-3 btn-brutal-shadow inline-flex items-center gap-2">Start free <ArrowRight size={16} /></Link>
          <Link to="/" className="px-6 py-3 border border-neutral-300">Back to home</Link>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {[
            { i: CalendarClock, t: "Class scheduling", d: "Demo, recurring, batch and one-to-one classes with auto-reminders." },
            { i: Users, t: "Student management", d: "Add students, manage fees, attendance and parents." },
            { i: ScrollText, t: "AI quiz maker", d: "Generate MCQs and assign in one click." },
            { i: BookOpen, t: "Lesson planner", d: "Full syllabus plans built by AI." },
            { i: BrainCircuit, t: "Post-class reports", d: "Engagement, confidence and weak topics, auto-detected." },
            { i: Sparkles, t: "Image & infographics", d: "Diagrams and slides generated from any topic." },
          ].map((f, i) => (
            <div key={i} className="bg-white p-6">
              <f.i size={26} className="text-[#002FA7]" strokeWidth={1.5} />
              <div className="font-display font-semibold mt-4">{f.t}</div>
              <div className="text-sm text-neutral-600 mt-1">{f.d}</div>
            </div>
          ))}
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
